import SocketUtil from './socketUtil';

export default class ConferenceSocket {
  socket = null;

  status = '0';

  options = {
    id: null,
    roomId: null,
  };

  constructor({ id, roomId }) {
    this.options = {
      ...this.options,
      id,
      roomId,
    };
  }

  startSocket = () => {
    const socket = new SocketUtil('/ws/tscloud-eq-interact/endpoint', {
      complete: () => {
        this.subscribeConferences();
        this.subscribeRoomEquipments();
        this.onComplete();
      },
      failback: () => {
        this.onFailback();
      },
      before: () => {
        this.onBefore();
      },
    });
    this.socket = socket;
    return socket;
  };

  subscribeConferences = () => {
    const { id } = this.options;
    const { conferencesResponse } = this;
    this.socket.subscribe(`/topic/conferences/${id}`, (data) => {
      if (data && data.body.indexOf(`method`) !== -1) {
        const msg = JSON.parse(data.body);
        conferencesResponse(msg);
      }
    });
  };

  subscribeRoomEquipments = () => {
    const { roomId } = this.options;
    const { roomEquipmentsResponse } = this;
    this.socket.subscribe(`/topic/roomequipments/${roomId}`, (data) => {
      if (data && data.body.indexOf(`method`) !== -1) {
        const msg = JSON.parse(data.body);
        roomEquipmentsResponse(msg);
      }
    });
  };

  conferencesResponse = (data) => {
    const { method, params } = data;
    this.onMethod({ method, params });
    console.log('================================', params);
    // 会议开始
    if (method === `conference_create` || method === `conference_update`) {
      const {
        endTime,
        startTime,
        autoSpeak,
        platLiveStatus: platLive,
        platRecordStatus: platRecord,
        duration,
      } = params;
      if (this.status === '0') {
        this.onConferenceStart();
      }
      this.status = '1';
      this.onConferenceUpdate({ status: this.status, endTime, startTime, duration });
      this.onAutoSpeak({ autoSpeak });
      this.onPlatLive({ platLive });
      this.onPlatRecord({ platRecord });
    }
    // 会议结束
    if (method === `conference_delete`) {
      if (this.status === '1') {
        this.onConferenceEnd();
      }
      this.onConferenceUpdate({ status: this.status });
    }
    // 与会者创建
    if (method === `party_create` || method === `party_update`) {
      const { roomId, name, state: connected, audioMute, speaking, lecturer } = params;
      this.onMemberUpdate({ roomId, name, connected, audioMute, speaking, lecturer });
      this.onMemberSpeaks({ roomId, name, speaking, connected });
    }
    // 与会者删除
    if (method === `party_delete`) {
      const { roomId } = params;
      this.onMemberDelete({ roomId });
    }
    if (method === `alert`) {
      const { message } = params;
      this.warning({ message });
    }
    return null;
  };

  roomEquipmentsResponse = (data) => {
    const { method, params } = data;
    this.onMethod({ method, params });
    console.log('================================', params);
    if (method === `task_status`) {
      const { innerLive: localLive, recStatus: localRecord, recordMode } = params;
      // 本地直播
      this.onLocalLive({ localLive });
      // 本地录制
      this.onLocalRecord({ localRecord });
      // 录制模式
      this.onRecordMode({ recordMode });
    }
    if (method === `track_status`) {
      const { trackMode } = params;
      // 自动跟踪
      this.onTrackMode({ trackMode });
    }
    if (method === `double_cont_stus`) {
      const { result: doubleContent } = params;
      // 双流
      this.onDoubleContent({ doubleContent });
    }
    if (method === `hasTarget`) {
      const { action: target } = params;
      // 主讲点名
      this.onTarget({ target });
    }
    if (method === `pic_status`) {
      const { splitPolicy: policyCode, pipPos } = params;
      // 分屏画面
      this.onSplitScreen({ policyCode, pipPos });
    }
    if (method === `video_mask`) {
      const { subTitle: captions } = params;
      // 字幕
      this.onCaptions({ captions });
    }
    if (method === `hasTarget`) {
      const { action: target, majorRole } = params;
      if (['sq', 'st'].includes(majorRole)) {
        this.onTarget({ target });
      }
    }
    return null;
  };

  send = async (method, param) => {
    const { errorMsg = () => {} } = this;
    if (this.socket && this.socket.status) {
      this.beforeSend({ method, param });
      const response = await this.socket.send(method, { ...param });
      if (response && response.error && response.error.code !== '0') {
        const theMessage = response.error.message || '未知错误';
        console.error(theMessage, response);
        errorMsg(theMessage);
      }
    }
  };

  // 刷新状态
  setSyncStatus = (id = this.options.id) => {
    this.send('syncStatus', { id });
  };

  // 加时
  setOverTime = ({ id = this.options.id, minutes }) => {
    this.send('overtime', { id, minutes });
  };

  // 开始会议
  startConference = (id = this.options.id) => {
    this.send('interact', { id, operation: 'start' });
  };

  // 结束会议
  endConference = (id = this.options.id) => {
    this.send('interact', { id, operation: 'end' });
  };

  setMute = ({ id = this.options.id, roomId, operation }) => {
    this.send('roomAudioMute', { id, roomId, operation });
  };

  setCall = ({ id = this.options.id, roomId }) => {
    this.send('connectRoom', { id, roomId });
  };

  setHangup = ({ id = this.options.id, roomId }) => {
    this.send('disConnectRoom', { id, roomId });
  };

  setSpeak = ({ id = this.options.id, roomId, operation }) => {
    this.send('callTheRole', { id, roomId, operation });
  };

  setTitle = ({ roomId, titleType, titleInfo }) => {
    this.send('setTitle', { roomId, titleType, titleInfo });
  };

  setSplitScreen = ({ roomId, policyId, pipPos }) => {
    this.send('splitScreen', { roomId, policyId, pipPos });
  };

  setPlatFormRecord = ({ id = this.options.id, operation }) => {
    this.send('platFormRecord', { id, operation });
  };

  setPlatFormLive = ({ id = this.options.id, operation }) => {
    this.send('platFormLive', { id, operation });
  };

  setDoubleContent = ({ roomId, operation }) => {
    this.send('doubleContent', { roomId, operation });
  };

  setAutoSpeak = ({ id = this.options.id, operation }) => {
    this.send('autoSpeak', { id, operation });
  };

  setTrackMode = ({ roomId, operation }) => {
    this.send('trackMode', { roomId, operation });
  };

  setVirtualRole = ({ roomId, operation }) => {
    this.send('setVirtualRole', { roomId, operation });
  };

  setLocalRecord = ({ roomId, operation }) => {
    this.send('localRecord', { roomId, operation });
  };

  onBefore = () => {};

  onFailback = () => {};

  onComplete = () => {};

  onConferenceUpdate = () => {};

  onAutoSpeak = () => {};

  onPlatLive = () => {};

  onPlatRecord = () => {};

  onMemberUpdate = () => {};

  onMemberSpeaks = () => {};

  onMemberDelete = () => {};

  onLocalLive = () => {};

  onLocalRecord = () => {};

  onRecordMode = () => {};

  onTrackMode = () => {};

  onDoubleContent = () => {};

  onTarget = () => {};

  onSplitScreen = () => {};

  onCaptions = () => {};

  onMethod = () => {};

  beforeSend = () => {};

  warning = () => {};
}
